import React from 'react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import ColumnQueryHeaders from '../components/common/ColumnQueryHeaders';
import { useColumnQuery } from '../components/common/columnQuery';
import type { TimeEntry } from '@/data/scheduleTimeTracking';
import { allocationLabel, hoursLabel, lunchLabel, recordedTimeColumns } from './recordedTimeColumns';

type Props = {
  entries: TimeEntry[];
  timeZone: string;
  jobPath: (entry: TimeEntry) => string;
  renderActions: (entry: TimeEntry) => React.ReactNode;
  /** The open modal dialog, so header menus render inside its top layer. */
  portalTarget: Element | null;
  /** Loads every page in the date range so filter values, counts and sorts are complete. */
  onNeedAllEntries: () => void;
  loadingAll: boolean;
};

export function ColumnQueryStatus({ query, total, loading }: { query: ReturnType<typeof useColumnQuery>; total: number; loading?: string | null }) {
  if (!query.querying && !loading) return null;
  return <div className="schedule-time__filter-status" role="status">
    <span>{loading || `Showing ${query.rows.length} of ${total} entries`}</span>
    {query.querying ? <button type="button" className="btn subtle xs" onClick={query.clear}>Clear filters</button> : null}
  </div>;
}

export default function RecordedTimeTable({ entries, timeZone, jobPath, renderActions, portalTarget, onNeedAllEntries, loadingAll }: Props) {
  const columns = React.useMemo(() => recordedTimeColumns(timeZone), [timeZone]);
  const query = useColumnQuery(entries, columns);
  React.useEffect(() => { onNeedAllEntries(); }, [onNeedAllEntries]);
  const formatTime = (value: string) => dayjs(value).tz(timeZone).format('MMM D, YYYY h:mm A');
  return <>
    <ColumnQueryStatus query={query} total={entries.length}
      loading={loadingAll ? `Loading every entry in this date range (${entries.length} so far)…` : null} />
    <div className="schedule-time__table-wrap"><table className="schedule-time__table schedule-time__recorded-table schedule-time__query-table" aria-label="Recorded job time">
      <thead><tr>
        <ColumnQueryHeaders columns={columns} query={query} portalTarget={portalTarget} />
        <th scope="col" className="schedule-time__actions-heading">Actions</th>
      </tr></thead>
      <tbody>{query.rows.map(entry => <tr key={entry.entry_id}>
        <td data-label="Person"><strong>{entry.person_name}</strong></td>
        <td data-label="Job"><Link to={jobPath(entry)}>{entry.job_code}</Link></td>
        <td data-label="Time in">{formatTime(entry.clock_in)}</td>
        <td data-label="Time out">{entry.clock_out ? formatTime(entry.clock_out) : 'Clocked in'}</td>
        <td data-label="Lunch">{lunchLabel(entry)}</td>
        <td data-label="Hours">{hoursLabel(entry)}</td>
        <td data-label="Work type">{allocationLabel(entry)}</td>
        <td data-label="Actions">{renderActions(entry)}</td>
      </tr>)}</tbody>
    </table></div>
    {query.querying && !loadingAll && entries.length > 0 && !query.rows.length ? <p role="status">No entries match these filters.</p> : null}
  </>;
}
